import type { Decimal } from './math/num';
import { D, ZERO } from './math/num';
import type { RngState } from './math/rng';
import { createRng } from './math/rng';
import type { MindGraph } from './graph/types';
import { createStarterGraph } from './graph/factory';
import type { ActiveEnemy } from './sim/spawn';
import type { PartInstance, Rarity } from './data/parts/partTypes';
import { FRAMES } from './data/frames';

/**
 * GameState — the one serializable object owned by the core. All quantities are
 * Decimal. Time and randomness live here as data so the sim is a pure function
 * of (state, action, dt).
 *
 * v3: parts/loot/weight rebuild — inventory of part instances, multi-enemy
 * combat, persisted attack cooldown timers, progressive-unlock flags, and the
 * CPU/RAM/Storage resource seam.
 */

export const SAVE_VERSION = 24;

export interface Settings {
  notation: 'standard' | 'scientific' | 'engineering';
  reducedMotion: boolean;
}

export interface Wallet {
  scrap: Decimal; // soft — frame levels
  components: Decimal; // part upgrades / fusing
  alloy: Decimal; // smelted parts — permanent capacity upgrades
}

export interface FrameState {
  defId: string;
  level: number;
  hp: Decimal;
}

/** Looted parts. Cap is the Storage seam. */
export interface Inventory {
  parts: PartInstance[];
  nextId: number;
  cap: number;
  /** Instance ids the player has locked: never scrapped, melted, or forged. */
  protectedIds: string[];
}

/** System resources. CPU and Storage are live limits (grown by the Grid);
 *  ram is INERT since P57 (wiring is never capped; the booster stacking
 *  penalty is the lever) and stays only for save-shape stability. */
export interface SystemResources {
  cpu: number; // max active attacks
  ram: number; // unused since P57 (kept for save compatibility)
  storage: number; // inventory cap basis
}

export interface CombatState {
  wave: number;
  highestWave: number;
  checkpoint: number;
  enemies: ActiveEnemy[];
  nextEnemyId: number;
  /** nodeId -> ms remaining until that attack is ready (persisted). */
  attackTimers: Record<string, number>;
  killsThisRun: number;
  // Maximum Effort + Full Send cooldown clocks (elapsedMs deadlines). Transient:
  // short enough that surviving a reload is meaningless, so they are NOT
  // serialized and reset to ready (0) on load.
  maxEffortUntil: number;
  maxEffortCdUntil: number;
  fullSendCdUntil: number;
  /** true (default): clearing a wave advances to the next. false: the current
   *  wave respawns in place so you can farm it (the "hold/pause" toggle). */
  autoAdvance: boolean;
  /** Cached live DPS estimate for display/tests. */
  currentDps: Decimal;
  /** Arena pause: 'defeat' = reboot countdown, 'victory' = post-overlord breather. */
  pauseKind: 'defeat' | 'victory' | 'duelIntro' | null;
  /** elapsedMs when the pause lifts and combat resumes. */
  pauseUntilMs: number;
  /** Active overlord duel; the arena hosts the overlord instead of waves. */
  overlordId: string | null;
  /** Ms the CURRENT encounter has lasted — drives Enrage (enemy damage ramps
   *  the longer a fight drags; resets every fresh wave/duel). */
  enrageMs: number;
}

export interface LifetimeStats {
  totalKills: number;
  bestWave: number;
}

export interface MetaState {
  cores: Decimal;
  ascensions: number;
  /** Reboots performed at max chassis (the post-Sol endless Core loop). Each
   *  satellite launched grants one reboot; a completed swarm lifts the cap. */
  reboots: number;
  lifetimeScrap: Decimal;
  /** Permanent Core upgrades (id -> level). Bought with Cores; persist forever. */
  coreUpgrades: Record<string, number>;
  /** Progressive-disclosure flags (which tabs/systems are revealed). */
  unlocks: Record<string, boolean>;
}

/** A returned Strike Sortie's haul, awaiting collection. */
export interface SortieResult {
  components: Decimal;
  cache: PartInstance | null;
  broke: number;
}

/** An in-flight or returned Strike Sortie. */
export interface DroneSortie {
  tier: number;
  dronesSent: number;
  endsMs: number;
  status: 'running' | 'returned';
  result: SortieResult | null;
}

/** Salvage Drone Bay — first Tech Find (unlocks on the first Ascension). */
export interface DroneState {
  unlocked: boolean;
  bots: number;
  broken: number;
  clearance: number;
  sortie: DroneSortie | null;
}

/** AI Overlords — the bosses that gate Ascension. Defeated ids persist forever. */
export interface OverlordsState {
  defeated: string[];
  /** Lifetime kills per overlord id (re-fights count). Every 5 defeats of an
   *  overlord earns/levels its mini pet (data/pets.ts OVERLORD_MINI_DEFS). */
  defeats: Record<string, number>;
}

/** Pets — rare drops off wave mobs (unlocked by beating DJ Leggs). The
 *  collection maps enemy modelId -> drops collected (level = min(10, drops)). */
export interface PetsState {
  unlocked: boolean;
  collection: Record<string, number>;
  equipped: string[];
}

/** A fusion cooking in the Foundry: 3× (defId @ rarity) -> 1× next rarity. */
export interface FoundryJob {
  defId: string;
  /** INPUT rarity; the output is one rung above. */
  rarity: Rarity;
  endsMs: number;
}

/** The Foundry — part fusion (unlocked by beating Sir Loin). */
export interface FoundryState {
  unlocked: boolean;
  job: FoundryJob | null;
  /** Lifetime fusions completed (stats/flavor). */
  fused: number;
}

/** A pet running an obedience trial in the Training Yard. */
export interface YardTrial {
  petId: string;
  trialId: string;
  endsMs: number;
}

/** The Training Yard — pet trials (unlocked by beating Sparky Boi). */
export interface YardState {
  unlocked: boolean;
  level: number;
  trials: YardTrial[];
  /** Lifetime trials completed (stats/flavor). */
  trained: number;
}

/** A part in the melt queue (the instance is gone; this is what's left of it). */
export interface SmeltItem {
  defId: string;
  rarity: Rarity;
  level: number;
}

/** The Smelter — parts to Alloy to capacity (unlocked by beating Sir Cumference). */
export interface SmelterState {
  unlocked: boolean;
  queue: SmeltItem[];
  /** elapsedMs when the head of the queue finishes melting. */
  nextDoneMs: number;
  /** Permanent capacity ranks (id -> rank). */
  upgrades: Record<string, number>;
  /** Lifetime parts melted (stats/flavor). */
  smelted: number;
}

/** Track between two depots (cleared segments). Derailed until the timestamp. */
export interface RailRoute {
  a: number;
  b: number;
  derailedUntilMs: number;
}

/** Supply Lines — rail logistics (unlocked by beating Sir Pent). */
export interface RailsState {
  unlocked: boolean;
  routes: RailRoute[];
  /** elapsedMs of the last derailment roll. */
  lastDerailMs: number;
  /** Lifetime routes laid (stats/flavor). */
  built: number;
}

/** Compute Grid — Intrusion Protocols + permanent ranks (unlocked by beating
 *  MechCorp Data Center). Compute raises CPU/Storage and buys Lattice
 *  Conductance (upgrades['cond']), which eases the booster stacking penalty. */
export interface GridState {
  unlocked: boolean;
  /** The intrusion currently running (one at a time). */
  run: { targetId: string; endsMs: number } | null;
  compute: Decimal;
  /** Capacity ranks bought (id -> rank), for pricing. */
  upgrades: Record<string, number>;
  /** Lifetime intrusions completed (stats/flavor). */
  intrusions: number;
}

/** Districts — citizens staff the city-mech (unlocked by beating MechCorp City).
 *  Population derives from wave progress past baselineWave; only assignments
 *  and grants are stored. */
export interface DistrictsState {
  unlocked: boolean;
  /** highestWave when the city powered up (rescues count from here). */
  baselineWave: number;
  assigned: { housing: number; factory: number; lab: number; arcade: number };
  /** Admin/event grants on top of derived population. */
  popBonus: number;
}

/** Star Harvest — the Dyson swarm megaproject (unlocked by beating Sol).
 *  Satellites boost Core power; 50 completes the swarm and reveals Reboot. */
export interface HarvestState {
  unlocked: boolean;
  satellites: number;
  /** The launch on the pad (one at a time). */
  launch: { endsMs: number } | null;
}

/** Hostile Takeover — the dividends desk (unlocked by beating MechCorp HQ). */
export interface TakeoverState {
  unlocked: boolean;
  /** Division id -> stake level (0..10; 10 = bought out, permanent). */
  stakes: Record<string, number>;
  /** elapsedMs of the next dividend payout (0 = not scheduled yet). */
  nextPayoutMs: number;
  /** elapsedMs of the last free reallocation. */
  lastShiftMs: number;
  /** Last dividend paid (display). */
  lastPayout: Decimal;
  /** Lifetime payouts (stats/flavor). */
  payouts: number;
}

export interface GameState {
  version: number;
  lastTickAt: number;
  elapsedMs: number;
  rng: RngState;
  settings: Settings;
  wallet: Wallet;
  frame: FrameState;
  inventory: Inventory;
  resources: SystemResources;
  mind: MindGraph;
  combat: CombatState;
  stats: LifetimeStats;
  meta: MetaState;
  drones: DroneState;
  overlords: OverlordsState;
  pets: PetsState;
  foundry: FoundryState;
  yard: YardState;
  smelter: SmelterState;
  rails: RailsState;
  takeover: TakeoverState;
  grid: GridState;
  districts: DistrictsState;
  harvest: HarvestState;
}

export interface NewGameOptions {
  seed?: number;
  now?: number;
}

export const DEFAULT_RESOURCES: SystemResources = { cpu: 6, ram: 12, storage: 60 };

export function createInitialState(opts: NewGameOptions = {}): GameState {
  const seed = opts.seed ?? 0x1a2b3c4d;
  const now = opts.now ?? 0;
  const firstFrame = FRAMES[0]!;
  const starter = createStarterGraph();
  return {
    version: SAVE_VERSION,
    lastTickAt: now,
    elapsedMs: 0,
    rng: createRng(seed),
    settings: { notation: 'standard', reducedMotion: false },
    wallet: { scrap: ZERO, components: ZERO, alloy: ZERO },
    frame: { defId: firstFrame.id, level: 1, hp: D(firstFrame.baseHp) },
    inventory: { parts: starter.parts, nextId: starter.nextPartId, cap: DEFAULT_RESOURCES.storage, protectedIds: [] },
    resources: { ...DEFAULT_RESOURCES },
    mind: starter.graph,
    combat: {
      wave: 1,
      highestWave: 1,
      checkpoint: 1,
      enemies: [],
      nextEnemyId: 1,
      attackTimers: {},
      killsThisRun: 0,
      maxEffortUntil: 0,
      maxEffortCdUntil: 0,
      fullSendCdUntil: 0,
      autoAdvance: true,
      currentDps: ZERO,
      pauseKind: null,
      pauseUntilMs: 0,
      overlordId: null,
      enrageMs: 0,
    },
    stats: { totalKills: 0, bestWave: 1 },
    meta: { cores: ZERO, ascensions: 0, reboots: 0, lifetimeScrap: ZERO, coreUpgrades: {}, unlocks: { mind: true, salvage: true } },
    drones: { unlocked: false, bots: 0, broken: 0, clearance: 1, sortie: null },
    overlords: { defeated: [], defeats: {} },
    pets: { unlocked: false, collection: {}, equipped: [] },
    foundry: { unlocked: false, job: null, fused: 0 },
    yard: { unlocked: false, level: 1, trials: [], trained: 0 },
    smelter: { unlocked: false, queue: [], nextDoneMs: 0, upgrades: {}, smelted: 0 },
    rails: { unlocked: false, routes: [], lastDerailMs: 0, built: 0 },
    takeover: { unlocked: false, stakes: {}, nextPayoutMs: 0, lastShiftMs: 0, lastPayout: ZERO, payouts: 0 },
    grid: { unlocked: false, run: null, compute: ZERO, upgrades: {}, intrusions: 0 },
    districts: { unlocked: false, baselineWave: 1, assigned: { housing: 0, factory: 0, lab: 0, arcade: 0 }, popBonus: 0 },
    harvest: { unlocked: false, satellites: 0, launch: null },
  };
}
